import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Phone, Clock, MessageCircle, Video, ShieldCheck, ArrowRight, ClipboardList, CreditCard, PhoneCall } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AstrologerCards } from "@/components/AstrologerCards";

const INCLUDES = [
  { icon: PhoneCall, text: "One-on-one session with an experienced astrologer" },
  { icon: Video, text: "Choose Call, WhatsApp or Video Call" },
  { icon: Clock, text: "Astrologer responds within 24 hours of booking" },
  { icon: ShieldCheck, text: "Private & confidential — your details stay with us" },
];

const FLOW = [
  { icon: ClipboardList, title: "Share your concern", desc: "Topic, birth details & preferred contact mode." },
  { icon: CreditCard, title: "Confirm & pay", desc: "Transparent fee with GST shown before payment." },
  { icon: MessageCircle, title: "Astrologer connects", desc: "We reach out within 24 hours to schedule." },
];

export function ConsultSection({ fee = 999, gst = 18 }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const gstAmt = +(fee * gst / 100).toFixed(2);
  const total = +(fee + gstAmt).toFixed(2);
  const book = () => nav(user ? "/dashboard#consult" : "/register?next=consult");

  return (
    <section id="consult" data-testid="home-consult-section" className="relative py-14 sm:py-24 overflow-hidden">
      <div className="orb w-[460px] h-[460px] -left-24 top-10 bg-purple-700/12" />
      <div className="orb w-[380px] h-[380px] right-0 bottom-0 bg-amber-500/8" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-10 lg:gap-12 lg:grid-cols-[1.1fr_0.9fr] items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/25 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-amber-400/90">
            <Phone size={14} /> Premium Consultation
          </span>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-slate-50 leading-tight">
            Consult an Astrologer, <span className="gold-text">Personally.</span>
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed max-w-xl">
            Need deeper guidance on career, marriage, finance or a specific life decision? Book a dedicated
            session and speak directly with an astrologer about your birth chart.
          </p>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {INCLUDES.map((it, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-300" data-testid={`consult-include-${i}`}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-300"><it.icon size={15} /></span>
                <span className="pt-1.5 leading-snug">{it.text}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 sm:mt-10 grid gap-4 grid-cols-1 sm:grid-cols-3">
            {FLOW.map((f, i) => (
              <div key={f.title} className="relative" data-testid={`consult-step-${i}`}>
                <span className="font-serif text-4xl text-amber-500/20">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-1 text-sm font-semibold text-amber-100 flex items-center gap-2"><f.icon size={14} className="text-amber-300" /> {f.title}</p>
                <p className="mt-1 text-xs text-slate-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
          className="relative p-[1px] rounded-3xl bg-gradient-to-br from-amber-400/50 via-amber-200/10 to-purple-500/40">
          <div className="rounded-3xl bg-[#0E132A] p-6 sm:p-10" data-testid="consult-price-card">
            <p className="text-xs uppercase tracking-[0.2em] text-amber-400/90">Consultation Fee</p>
            <p className="mt-3 font-serif text-5xl text-amber-100" data-testid="home-consult-fee">₹{fee}</p>
            <p className="mt-1 text-sm text-slate-400">+ {gst}% GST per session</p>
            <div className="mt-6 rounded-xl bg-amber-500/5 border border-amber-500/15 px-4 py-3 text-sm text-slate-300 space-y-1.5">
              <div className="flex justify-between"><span>Consultation fee</span><span>₹{fee.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>GST ({gst}%)</span><span>₹{gstAmt.toFixed(2)}</span></div>
              <div className="flex justify-between text-amber-100 font-semibold border-t border-amber-500/15 pt-1.5"><span>Total payable</span><span data-testid="home-consult-total">₹{total.toFixed(2)}</span></div>
            </div>
            <button data-testid="home-consult-book-btn" onClick={book}
              className="btn-gold rounded-full w-full mt-7 py-3.5 text-base flex items-center justify-center gap-2">
              Book a Consultation <ArrowRight size={18} />
            </button>
            <p className="mt-4 text-center text-xs text-slate-500">
              {user ? "You'll be taken to your dashboard to complete the booking." : "Create a free account to book — takes under a minute."}
            </p>
          </div>
        </motion.div>
      </div>
      <AstrologerCards />
    </section>
  );
}
